/**
 * Валидация пары «фундаментальный многоугольник + схема склейки».
 *
 * Прежде чем строить образующие фуксовой группы и создавать карту, необходимо
 * убедиться, что заданная комбинация многоугольника и склейки в принципе может
 * образовать корректную замкнутую поверхность. Проверки здесь — чисто
 * структурные (над словом склейки и числом сторон), детерминированные и без DOM.
 *
 * Соглашение о слове склейки (как в `abABcdCD` для рода 2):
 * - строчная буква обозначает сторону, заглавная — её обратную (инверсию);
 * - каждая сторона участвует в отождествлении ровно с одной парной стороной,
 *   поэтому каждая буква (без учёта регистра) должна встречаться ровно дважды;
 * - пара `x…X` (строчная + заглавная) отождествляет стороны с сохранением
 *   ориентации; пара `x…x` (две одинаковые строчные) — с обращением ориентации,
 *   и тогда поверхность неориентируема (Требование 8.1). Обе разновидности пар
 *   допустимы;
 * - число сторон обязано быть чётным, иначе не все стороны можно склеить.
 *
 * Неориентируемая замкнутая поверхность (в слове есть хотя бы одна пара `x…x`)
 * принимается при выполнении условий Требования 8.3: каждая буква встречается
 * ровно дважды, число сторон чётное и лежит в диапазоне `[2, 64]`. Нарушение
 * любого условия ведёт к отклонению с указанием конкретной причины
 * (Требование 8.6). Признак ориентируемости возвращается в успешном результате
 * полем `orientable`.
 *
 * Функция возвращает структурированный результат (а не бросает исключение для
 * ожидаемых ошибок валидации), чтобы серверные роуты могли превратить его в
 * ответ 400 с понятным сообщением и не создавать карту.
 *
 * Помимо структурных проверок над словом склейки, валидатор классифицирует тип
 * геометрии по сумме внутренних углов (гиперболическая / евклидова /
 * сферическая) и, при успешной классификации, выбирает конкретную реализацию
 * {@link Geometry} через {@link createGeometry}. Евклидовы и сферические
 * конфигурации принимаются только при чётном числе сторон `n ∈ [4, 12]`; при
 * нечётном `n`, `n` вне `[4, 12]` или сумме углов, не соответствующей ни одному
 * классу, конфигурация отклоняется с указанием причины (Требования 6.1, 6.2,
 * 6.8). Классификатор {@link classifyGeometry} бросает исключение при
 * несоответствии — валидатор перехватывает его и превращает в элемент `errors`,
 * поэтому наружу исключение не выходит (возвращается результат-объект), а
 * вызывающий код может сохранить предыдущую валидную конфигурацию без изменений.
 *
 * Требования: 2.6, 6.1, 6.2, 6.8, 8.1, 8.3, 8.6
 */

import type { FundamentalPolygon, GeometryType, SidePairingSpec } from './map-types.js';
import { classifyGeometry } from './fundamental-polygon.js';
import { createGeometry, type Geometry } from './geometry.js';

/**
 * Результат валидации геометрии карты.
 * Дискриминируется полем `valid`: при `false` в `errors` перечислены
 * понятные человеку сообщения обо всех найденных проблемах. При `true`
 * дополнительно возвращаются классифицированный тип геометрии, выбранная
 * реализация {@link Geometry} (по {@link MapConfig.geometryType}) и признак
 * ориентируемости поверхности `orientable` (Требование 8.1): `false`, если
 * слово склейки содержит хотя бы одну пару одинаковых строчных букв (`x…x`).
 */
export type MapValidationResult =
  | {
      valid: true;
      geometryType: GeometryType;
      geometry: Geometry;
      orientable: boolean;
    }
  | { valid: false; errors: string[] };

/** Минимальное число сторон неориентируемой замкнутой поверхности (Требование 8.3). */
export const NON_ORIENTABLE_MIN_SIDES = 2;

/** Максимальное число сторон неориентируемой замкнутой поверхности (Требование 8.3). */
export const NON_ORIENTABLE_MAX_SIDES = 64;

/**
 * Проверить, является ли символ буквой латинского алфавита (`a`–`z`/`A`–`Z`).
 */
function isLatinLetter(ch: string): boolean {
  return /^[a-zA-Z]$/.test(ch);
}

/**
 * Определить, содержит ли слово склейки хотя бы одну обращающую ориентацию пару
 * `x…x` — две одинаковые строчные буквы без парной заглавной (Требование 8.1).
 *
 * Соглашение согласовано с парсером `parsePairing` в `fuchsian-group.ts`: пара
 * считается обращающей ориентацию, если буква встречается ровно дважды и оба
 * вхождения строчные (заглавных вхождений этой буквы нет). Пары `x…X` (одна
 * строчная + одна заглавная) сохраняют ориентацию. Прочие (не-латинские)
 * символы игнорируются — их некорректность обрабатывается отдельной проверкой
 * на недопустимые символы.
 *
 * @param word слово склейки
 * @returns `true`, если найдена хотя бы одна пара `x…x` (поверхность неориентируема)
 */
function hasOrientationReversingPair(word: string): boolean {
  const lowerCounts = new Map<string, number>();
  const upperCounts = new Map<string, number>();
  for (const ch of word) {
    if (/^[a-z]$/.test(ch)) {
      lowerCounts.set(ch, (lowerCounts.get(ch) ?? 0) + 1);
    } else if (/^[A-Z]$/.test(ch)) {
      const key = ch.toLowerCase();
      upperCounts.set(key, (upperCounts.get(key) ?? 0) + 1);
    }
  }
  for (const [letter, lowerCount] of lowerCounts) {
    if (lowerCount === 2 && (upperCounts.get(letter) ?? 0) === 0) {
      return true;
    }
  }
  return false;
}

/**
 * Проверить корректность пары «многоугольник + склейка».
 *
 * Выполняемые проверки:
 * 1. Слово склейки непусто, является строкой и состоит только из латинских букв.
 * 2. Ориентируемость определяется по слову: наличие хотя бы одной пары `x…x`
 *    делает поверхность неориентируемой (Требование 8.1).
 * 3. Число сторон целое и в допустимом диапазоне: `[2, 64]` для неориентируемой
 *    поверхности (Требования 8.3, 8.6), `≥ 3` для ориентируемой.
 * 4. Массив вершин, если задан, согласован с числом сторон.
 * 5. Длина слова склейки равна числу сторон многоугольника.
 * 6. Число сторон чётное (стороны склеиваются попарно).
 * 7. Каждая буква (без учёта регистра) встречается в слове ровно дважды —
 *    т.е. каждая сторона имеет ровно одну парную и нет «висячих» сторон.
 *    Допустимы обе разновидности пар: `x…X` и `x…x` (Требование 8.3).
 * 8. Классификация типа геометрии по сумме внутренних углов: гиперболическая
 *    (сумма `< (n−2)π`), евклидова (сумма `= (n−2)π`) или сферическая (сумма
 *    в `((n−2)π, nπ]`). Евклидовы/сферические конфигурации принимаются только
 *    при чётном `n ∈ [4, 12]`; иначе — отклонение с указанием причины
 *    (Требования 6.1, 6.2, 6.8).
 *
 * @param polygon фундаментальный многоугольник
 * @param pairing схема склейки сторон
 * @returns при корректной конфигурации
 *   `{ valid: true, geometryType, geometry, orientable }` с классифицированным
 *   типом, выбранной реализацией {@link Geometry} и признаком ориентируемости;
 *   иначе `{ valid: false, errors }` со всеми найденными ошибками
 */
export function validateMapGeometry(
  polygon: FundamentalPolygon | null | undefined,
  pairing: SidePairingSpec | null | undefined,
): MapValidationResult {
  const errors: string[] = [];

  if (!polygon) {
    errors.push('Не задан фундаментальный многоугольник.');
  }
  if (!pairing) {
    errors.push('Не задана схема склейки сторон.');
  }
  if (!polygon || !pairing) {
    return { valid: false, errors };
  }

  const sides = polygon.sides;
  const word = pairing.word;

  // Слово склейки задано и является непустой строкой — без него дальнейшие
  // проверки (ориентируемость, парность, длина, диапазон сторон) невозможны.
  if (typeof word !== 'string' || word.length === 0) {
    errors.push('Слово склейки не задано или пусто.');
    return { valid: false, errors };
  }

  // Слово склейки состоит только из латинских букв.
  const invalidChars = Array.from(new Set(
    Array.from(word).filter((ch) => !isLatinLetter(ch)),
  ));
  if (invalidChars.length > 0) {
    errors.push(
      `Слово склейки содержит недопустимые символы: ${invalidChars
        .map((ch) => `«${ch}»`)
        .join(', ')}. Допустимы только латинские буквы (строчная — сторона, заглавная — её обратная).`,
    );
  }

  // Ориентируемость поверхности (Требование 8.1): конфигурация неориентируема,
  // если слово склейки содержит хотя бы одну пару одинаковых строчных букв
  // (`x…x`) без парной заглавной — такая пара задаёт отождествление сторон с
  // обращением ориентации. Пары `x…X` (строчная + заглавная) сохраняют
  // ориентацию. Обе разновидности пар допустимы (Требование 8.3).
  const orientable = !hasOrientationReversingPair(word);

  // 1. Число сторон многоугольника: целое и в допустимом диапазоне. Диапазон
  //    зависит от ориентируемости: для неориентируемой замкнутой поверхности
  //    `n ∈ [2, 64]` (Требования 8.3, 8.6); для ориентируемой — базовое
  //    требование `n ≥ 3` (без верхней границы).
  const sidesIsInteger = Number.isInteger(sides) && Number.isFinite(sides);
  let sidesInRange = false;
  if (!sidesIsInteger) {
    errors.push(
      `Число сторон многоугольника должно быть целым числом (получено: ${sides}).`,
    );
  } else if (!orientable) {
    sidesInRange =
      sides >= NON_ORIENTABLE_MIN_SIDES && sides <= NON_ORIENTABLE_MAX_SIDES;
    if (!sidesInRange) {
      errors.push(
        `Для неориентируемой поверхности число сторон должно быть в диапазоне ` +
          `[${NON_ORIENTABLE_MIN_SIDES}, ${NON_ORIENTABLE_MAX_SIDES}] (получено: ${sides}).`,
      );
    }
  } else {
    sidesInRange = sides >= 3;
    if (!sidesInRange) {
      errors.push(
        `Число сторон многоугольника должно быть целым и не меньше 3 (получено: ${sides}).`,
      );
    }
  }
  // Число сторон пригодно для геометрических шагов, только если оно целое и
  // попадает в допустимый (зависящий от ориентируемости) диапазон.
  const sidesIsValidInteger = sidesIsInteger && sidesInRange;

  // 2. Согласованность массива вершин с числом сторон.
  if (
    sidesIsInteger &&
    Array.isArray(polygon.vertices) &&
    polygon.vertices.length !== sides
  ) {
    errors.push(
      `Число вершин (${polygon.vertices.length}) не совпадает с числом сторон (${sides}).`,
    );
  }

  // 3. Длина слова склейки равна числу сторон.
  if (sidesIsInteger && word.length !== sides) {
    errors.push(
      `Длина слова склейки (${word.length}) должна быть равна числу сторон многоугольника (${sides}).`,
    );
  }

  // 4. Число сторон должно быть чётным (стороны склеиваются попарно).
  if (sidesIsInteger && sides % 2 !== 0) {
    errors.push(
      `Число сторон должно быть чётным, чтобы склеить все стороны попарно (получено нечётное: ${sides}).`,
    );
  }

  // 5. Каждая буква (без учёта регистра) должна встречаться ровно дважды.
  //    Проверка пар смягчена (Требование 8.3): допустимы обе разновидности —
  //    `x…X` (сохранение ориентации) и `x…x` (обращение ориентации,
  //    неориентируемая поверхность), поскольку счётчик регистронезависимый.
  if (invalidChars.length === 0) {
    const counts = new Map<string, number>();
    for (const ch of word) {
      const key = ch.toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    const unpaired: string[] = [];
    const overused: string[] = [];
    for (const [letter, count] of counts) {
      if (count === 1) {
        unpaired.push(letter);
      } else if (count > 2) {
        overused.push(`«${letter}» — ${count} раз`);
      }
    }

    if (unpaired.length > 0) {
      errors.push(
        `Каждая сторона должна иметь ровно одну парную. Не имеют пары (встречаются один раз): ${unpaired
          .map((l) => `«${l}»`)
          .join(', ')}.`,
      );
    }
    if (overused.length > 0) {
      errors.push(
        `Каждая буква должна встречаться ровно дважды, но встречается чаще: ${overused.join(', ')}.`,
      );
    }
  }

  // 6. Классификация типа геометрии по сумме внутренних углов и выбор
  //    реализации Geometry. Классификатор бросает исключение для конфигураций,
  //    не попадающих ни в один класс (нечётное n или n вне [4, 12] для
  //    евклидовой/сферической суммы, сумма сверх сферического максимума nπ) —
  //    перехватываем его и превращаем в понятную причину отклонения, не
  //    пропуская исключение наружу (Требования 6.1, 6.2, 6.8).
  let geometryType: GeometryType | null = null;
  if (sidesIsValidInteger) {
    try {
      geometryType = classifyGeometry(polygon.angleSum, sides);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      errors.push(
        `Не удалось классифицировать геометрию конфигурации: ${reason}.`,
      );
    }
  }

  if (errors.length > 0 || geometryType === null) {
    return { valid: false, errors };
  }

  // Выбор конкретной реализации Geometry по классифицированному типу
  // (совпадает с MapConfig.geometryType). Признак ориентируемости поверхности
  // (Требование 8.1) возвращается в успешном результате.
  return {
    valid: true,
    geometryType,
    geometry: createGeometry(geometryType),
    orientable,
  };
}

/**
 * Assert-обёртка над {@link validateMapGeometry}: бросает `Error` с перечнем
 * всех проблем, если пара «многоугольник + склейка» некорректна. Удобно для
 * мест, где невалидная конфигурация является ошибкой программиста, а не
 * ожидаемым пользовательским вводом.
 *
 * @throws Error если конфигурация невалидна
 */
export function assertValidMapGeometry(
  polygon: FundamentalPolygon | null | undefined,
  pairing: SidePairingSpec | null | undefined,
): void {
  const result = validateMapGeometry(polygon, pairing);
  if (!result.valid) {
    throw new Error(
      `Некорректная геометрия карты:\n- ${result.errors.join('\n- ')}`,
    );
  }
}
